import {defineStore} from 'pinia';
import {scrollTop} from "@js/helpers.ts";
import {usePage} from "@inertiajs/vue3";
import {markRaw} from 'vue'
import IconBath from "@js/components/Icons/IconBath.vue";
import IconSVP from "@js/components/Icons/IconSVP.vue";
import IconGrout from "@js/components/Icons/IconGrout.vue";

const toNum = (val) => typeof val === 'string' ? parseFloat(val) : val;

const tileCalc = {
    types: {
        wall: 'wall',
        floor: 'floor',
    },
    type: 'wall',
    calc_data: {
        room: {
            length: 1.9,
            width: 1.7,
            height: 3
        },
        bath: {
            length: 1700,
            width: 750,
            height: 450,
            is_adjacent_to_wall_on_three_sides: true,
            include_screen: true
        },
        door: {
            width: 700,
            height: 2000
        },
        tile: {
            length: 600,
            width: 300,
            price: 1000
        },
        extra: {
            seam_width: 1.5,
            layout: 'прямая раскладка',
            reserve: 5
        }
    },
    is_calculated: false,
    result: {
        wallsArea: 0,
        doorArea: 0,
        bathArea: 0,
        screenArea: 0,
        totalArea: 0,
        tileCount: 0,
        totalCost: 0
    },
    error_message: null,
}

const svpCalc = {
    is_calculated: false,
    error_message: null,
    calc_data: {
        tile_length: 100,
        tile_width: 100,
        facing_area: 1,
        wedges_count_in_box: 100,
        wedges_box_price: 10,
        clamp_count_in_box: 100,
        clamp_box_price: 10,
    },
    result: {
        totalWedges: 0,
        totalClamps: 0,
        wedgeBoxCount: 0,
        clampBoxCount: 0,
        wedgesCost: 0,
        clampsCost: 0
    }
}

const groutCalc = {
    is_calculated: false,
    error_message: null,
    calc_data: {
        grout_type: 'Цементная',
        tile_length: 100,
        tile_width: 100,
        tile_thickness: 6,
        facing_area: 10,
        seam_width: 2,
        grout_density: 1.7,
        grout_package_weight: 25,
        grout_package_price: 110
    },
    result: {
        avg_consumption_per_m2: 0,
        total_kg: 0,
        total_packages: 0,
        total_cost: 0
    }
}

export const useCalculatorStore = defineStore('calculatorStore', {
    state: () => ({
        calc_title: 'Калькулятор плитки в ванную комнату',
        calc_type: 'bath',
        calc_types: {
            bath: {
                title: 'Калькулятор плитки в ванную комнату',
                btn_title: 'Калькулятор ванной комнаты',
                icon: markRaw(IconBath),
            },
            svp: {
                title: 'Калькулятор СВП',
                btn_title: 'Калькулятор СВП',
                icon: markRaw(IconSVP),
            },
            grout: {
                title: 'Калькулятор расчета затирки (фуги) для швов',
                btn_title: 'Калькулятор расчета затирки',
                icon: markRaw(IconGrout),
            }
        },
        grout_types: {
            'Цементная': 1.7,
            'Эпоксидная': 1.55,
            'Полиуретановая': 1.35,
            'Силиконовая': 0.97
        },
        tile_calc: {...tileCalc},
        svp_calc: {...svpCalc},
        grout_calc: {...groutCalc},
    }),
    actions: {
        setType(newType) {
            const allowedTypes = Object.keys(this.calc_types);

            if (allowedTypes.includes(newType)) {
                this.calc_type = newType;
                this.calc_title = this.calc_types[newType].title
            } else {
                this.calc_type = 'bath'
                this.calc_title = this.calc_types.bath.title
            }
        },
        tileCalculate() {
            this.tile_calc.error_message = null;

            if (!this.validateTileCalc()) {
                this.tile_calc.is_calculated = false;
                this.tile_calc.error_message = 'Пожалуйста, заполните все поля корректно.';
                scrollTop();
                return;
            }

            const data = this.tile_calc.calc_data;
            const type = this.tile_calc.type;

            const room = {
                length: toNum(data.room.length),
                width: toNum(data.room.width),
                height: toNum(data.room.height),
            };

            const bath = {
                length: toNum(data.bath.length),
                width: toNum(data.bath.width),
                height: toNum(data.bath.height),
                is_adjacent_to_wall_on_three_sides: data.bath.is_adjacent_to_wall_on_three_sides,
                include_screen: data.bath.include_screen,
            };

            const door = {
                width: toNum(data.door.width),
                height: toNum(data.door.height),
            };

            const tile = {
                length: toNum(data.tile.length),
                width: toNum(data.tile.width),
                price: toNum(data.tile.price),
            };

            const extra = {
                seam_width: toNum(data.extra.seam_width),
                reserve: toNum(data.extra.reserve),
                layout: data.extra.layout,
            };

            let layoutReserve = 0;
            switch (extra.layout) {
                case 'со смещением': layoutReserve = 0.10; break;
                case 'по диагонали': layoutReserve = 0.15; break;
            }

            const reserve = (extra.reserve / 100) + layoutReserve;

            let totalArea = 0;
            let doorArea = 0;
            let bathArea = 0;
            let screenArea = 0;

            if (type === 'wall') {
                const perimeter = 2 * (room.length + room.width);
                const wallsArea = perimeter * room.height;

                doorArea = (door.width / 1000) * (door.height / 1000);

                const bathLong = (bath.length / 1000) * (bath.height / 1000);
                const bathShort = (bath.width / 1000) * (bath.height / 1000);
                bathArea = bath.is_adjacent_to_wall_on_three_sides
                    ? bathLong + bathShort * 2
                    : bathLong;

                screenArea = bath.include_screen ? bathLong : 0;

                const netWallArea = wallsArea - doorArea - bathArea;
                totalArea = +(netWallArea + netWallArea * reserve + screenArea).toFixed(2);

            } else if (type === 'floor') {
                const floorArea = room.length * room.width;
                totalArea = +(floorArea + floorArea * reserve).toFixed(2);
            }

            const tileArea = (tile.length / 1000) * (tile.width / 1000);
            const tileCount = Math.ceil(totalArea / tileArea);
            const totalCost = Math.round(tileCount * tileArea * tile.price);

            this.tile_calc.result = {
                totalArea,
                totalCost,
                tileCount,
                doorArea: +doorArea.toFixed(2),
                bathArea: +bathArea.toFixed(2),
                screenArea: +screenArea.toFixed(2)
            };

            this.tile_calc.is_calculated = true;
        },
        validateTileCalc() {
            const d = this.tile_calc.calc_data

            // Проверка общих значений
            if (d.room.length <= 0 || d.room.width <= 0) return false

            if (this.tile_calc.type === 'wall') {
                if (d.room.height <= 0) return false
                if (d.bath.height < 0 || d.bath.length < 0 || d.bath.width < 0) return false
                if (d.door.width < 0 || d.door.height < 0) return false
            }

            // Проверка плитки
            if (d.tile.length <= 0 || d.tile.width <= 0 || d.tile.price < 0) return false

            // Проверка доп. настроек
            if (d.extra.seam_width < 0 || d.extra.reserve < 0) return false

            return true
        },
        svpCalculate() {
            this.svp_calc.error_message = null;
            this.svp_calc.is_calculated = false;

            const data = this.svp_calc.calc_data;

            if (
                data.tile_length <= 0 || data.tile_width <= 0 || data.facing_area <= 0 ||
                data.wedges_count_in_box <= 0 || data.wedges_box_price < 0 ||
                data.clamp_count_in_box <= 0 || data.clamp_box_price < 0
            ) {
                this.svp_calc.error_message = 'Пожалуйста, заполните все поля.';
                scrollTop();
                return;
            }

            // Площадь одной плитки в м²
            const tileArea = (data.tile_length / 1000) * (data.tile_width / 1000);

            // Примерная формула: 2 СВП на 1 плитку
            const tileCount = Math.ceil(data.facing_area / tileArea);
            const totalClamps = tileCount * 2;
            const totalWedges = tileCount * 2;

            const wedgeBoxCount = Math.ceil(totalWedges / data.wedges_count_in_box);
            const clampBoxCount = Math.ceil(totalClamps / data.clamp_count_in_box);

            const wedgesCost = wedgeBoxCount * data.wedges_box_price;
            const clampsCost = clampBoxCount * data.clamp_box_price;

            this.svp_calc.result = {
                totalClamps,
                totalWedges,
                wedgeBoxCount,
                clampBoxCount,
                wedgesCost,
                clampsCost
            };

            this.svp_calc.is_calculated = true;
        },
        groutCalculate() {
            this.grout_calc.error_message = null;
            this.grout_calc.is_calculated = false;

            const d = this.grout_calc.calc_data;

            // Проверка на валидность всех полей
            const fields = [
                'tile_length', 'tile_width', 'tile_thickness',
                'facing_area', 'seam_width', 'grout_density',
                'grout_package_weight', 'grout_package_price'
            ];

            const invalid = fields.some(field => isNaN(+d[field]) || +d[field] <= 0);

            if (invalid) {
                this.grout_calc.error_message = 'Пожалуйста, заполните все поля корректно.';
                scrollTop();
                return;
            }

            // Извлекаем значения
            const A = +d.tile_length;       // мм
            const B = +d.tile_width;        // мм
            const C = +d.tile_thickness;    // мм
            const D = +d.seam_width;        // мм
            const S = +d.facing_area;       // м²
            const ρ = +d.grout_density;     // кг/дм³
            const weight = +d.grout_package_weight;
            const price = +d.grout_package_price;

            // Расчет среднего расхода на 1 м²
            const avg_consumption_per_m2 = +(((A + B) / (A * B)) * C * D * ρ).toFixed(2); // кг/м²

            // Общий расход
            const total_kg = +(avg_consumption_per_m2 * S).toFixed(2);
            const total_packages = Math.ceil(total_kg / weight);
            const total_cost = +(total_packages * price).toFixed(2);

            this.grout_calc.result = {
                avg_consumption_per_m2,
                total_kg,
                total_packages,
                total_cost
            };

            this.grout_calc.is_calculated = true;
        },
        hasUrlParams() {
            const page = usePage();
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
            return queryParams.toString().length > 0;
        },
        syncUrlParamsWithState() {
            if (!this.hasUrlParams()) return;

            const page = usePage();
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');

            this.tile_calc.calc_data.tile.length = queryParams.has('tile_length') ? queryParams.get('tile_length') : 600;
            this.tile_calc.calc_data.tile.width = queryParams.has('tile_width') ? queryParams.get('tile_width') : 300;
            this.tile_calc.calc_data.tile.price = queryParams.has('tile_price') ? queryParams.get('tile_price') : 1000;

            if (queryParams.has('type')) {
                this.setType(queryParams.get('type'))
            }
        },
        resetData() {
            this.tile_calc = {...tileCalc}
            this.svp_calc = {...svpCalc}
            this.grout_calc = {...groutCalc}
        }
    },
});
